import Dexie, { Table } from "dexie";

export type OptionValues = {
	amazonKey: string;
	typewiseKey: string;
	voiceType: string;
};

export interface Option {
	id?: number;
	name: string;
	value: string;
}

export interface Text {
	id?: number;
	tag: string;
	text: string;
	headline: string | null;
}

export interface HistoryItem {
	id?: number;
	text: string;
}

const errorLogger = (error: unknown) => console.log(`Oops something went wrong: ${error}`);

export class MySubClassedDexie extends Dexie {
	// 'keys' is added by dexie when declaring the stores()
	// We just tell the typing system this is the case
	options!: Table<Option>;
	texts!: Table<Text>;
	history!: Table<HistoryItem>;

	constructor() {
		super("patricipate");
		this.version(6).stores({
			options: "++id, name, value",
			texts: "++id, tag, text, headline",
			history: "++id, text",
		});
	}
}

const db = new MySubClassedDexie();

// manage saved texts
const addText = async (headline: Text["headline"], text: Text["text"], tag: Text["tag"]) => {
	if (!text) return;
	try {
		const id = await db.texts.add({ tag, text, headline });
		console.log({ tag, id, text, headline });
	} catch (error) {
		errorLogger(error);
	}
};

const getTexts = async (tag: Text["tag"]) => {
	try {
		const texts = await db.texts.where("tag").equals(tag).toArray();
		console.log(texts);
		return texts;
	} catch (error) {
		errorLogger(error);
	}
};

const deleteText = async (id: Text["id"]) => {
	try {
		const deleteCount = id && (await db.texts.delete(id));
		console.log("Deleted " + deleteCount + " objects");
	} catch (e) {
		errorLogger(e);
	}
};

// manage options
const putOptions = async (options: Option[]) => {
	try {
		options.map(({ id, name, value }) => {
			return id ? db.options.put({ id, name, value }) : db.options.add({ name, value });
		});
	} catch (error) {
		errorLogger(error);
	}
};

const getOptions = async () => {
	try {
		const options = await db.options.toArray();
		console.log(options);
		return options;
	} catch (error) {
		errorLogger(error);
	}
};

// manage history
const addHistoryItem = async (text: HistoryItem["text"]) => {
	if (!text) return;
	try {
		// Check the current count of history items
		const count = await db.history.count();

		// If the count exceeds 50, delete the oldest item(s)
		if (count >= 50) {
			const oldestItem = await db.history.orderBy("id").first();
			if (oldestItem?.id) {
				await db.history.delete(oldestItem.id);
			}
		}

		// Add the new history item
		const id = await db.history.add({ text });
		console.log({ id, text });
	} catch (error) {
		errorLogger(error);
	}
};

const getHistory = async () => {
	try {
		const history = await db.history.toArray();
		console.log(history);
		return history;
	} catch (error) {
		errorLogger(error);
	}
};

const deleteHistoryItem = async (id: HistoryItem["id"]) => {
	try {
		const deleteCount = id && (await db.history.delete(id));
		console.log("Deleted " + deleteCount + " objects");
	} catch (e) {
		errorLogger(e);
	}
};

const clearHistory = async () => {
	try {
		const deleteCount = await db.history.clear();
		console.log("Deleted " + deleteCount + " objects");
	} catch (e) {
		errorLogger(e);
	}
};

export {
	db,
	addText,
	getTexts,
	deleteText,
	putOptions,
	getOptions,
	addHistoryItem,
	getHistory,
	deleteHistoryItem,
	clearHistory,
};
